import { isEmpty, lowerCase } from "lodash";

import {
  AudienceAction,
  AudienceDto,
  AudienceFiltersDto,
  ExperienceType,
  ExpressionNodeQueryType,
  RedirectDto,
} from "@intelligems/ig-types";
import { hasStringDuplicates } from "@intelligems/ig-utils";

import {
  isJavaScriptExpressionValid,
  isUrlValid,
} from "../../../components/targeting/utils/validators";
import { postfixToInfix } from "../../../components/Utils/audienceExpressionConversion";
import { ProductPickerState } from "../../../store/types/productPicker";
import { ExperienceFe } from "../../../types/schemas/experiences";
import { VariationFe } from "../../../types/schemas/variations";
import { getSpecificRedirectsUtil } from "../components/redirects/getSpecificRedirects";

export const getDomainValue = (input: string, domain: string) => {
  let value: string;
  if (input === "") {
    value = input;
  } else if (domain.length === 0) {
    value = input;
  } else {
    try {
      const url = new URL(input);
      value = url.pathname.substring(1);
    } catch (e) {
      value = "";
    }
  }

  if (domain.length === 0) {
    return value;
  }

  value = value.split(`${domain}/`).pop() || "";
  value = value.includes(domain) ? "" : value;
  if (value.startsWith("/")) {
    value = value.substring(1);
  }
  return value;
};

export const trafficTargetingValidator = (
  audience: AudienceDto,
  variations: VariationFe[],
  domain = ""
): { isDisabled: boolean; reason: string | null } => {
  if (audience && audience.audienceType !== "common") {
    if (audience.filters.length > 0 && isEmpty(audience.elseAction)) {
      return {
        isDisabled: true,
        reason: "Select an audience targeting otherwise option",
      };
    }

    if (
      audience.filters.length > 0 &&
      audience.elseAction === "assignVariation" &&
      (isEmpty(audience.elseVariationId) ||
        (audience.elseVariationId &&
          !variations
            .map((group) => group.id)
            .includes(audience.elseVariationId)))
    ) {
      return {
        isDisabled: true,
        reason:
          "Select a test group for your audience targeting otherwise option",
      };
    }

    for (const queryParam of audience.filters) {
      if (queryParam.expression) {
        for (const expression of queryParam.expression) {
          if (expression.query) {
            if (
              isEmpty(queryParam.expression) ||
              // || isEmpty(queryParam.key) ||
              // isEmpty(queryParam.filter) ||
              (isEmpty(expression!.query!.value) &&
                expression!.query!.filter !== "isNull" &&
                expression!.query!.filter !== "isNotNull")
              // isEmpty(queryParam.action)
            ) {
              return {
                isDisabled: true,
                reason: "Complete all audience targeting conditions",
              };
            }
            if (expression.query.type === "jsExpression") {
              const errorMessage = isJavaScriptExpressionValid(
                expression.query.value
              ).errorMessage;
              if (errorMessage) {
                return {
                  isDisabled: true,
                  reason: "Javascript Expression not valid",
                };
              }
            }
            if (
              expression.query.type === "landingPage" &&
              (expression.query.filter === "equals" ||
                expression.query.filter === "doesNotEqual")
            ) {
              const errorMessage = isUrlValid(
                expression.query.value,
                domain
              ).errorMessage;
              if (errorMessage) {
                return {
                  isDisabled: true,
                  reason: "Landing Page not valid",
                };
              }
            }
          }
        }
      }
      if (
        queryParam.action === "assignVariation" &&
        (isEmpty(queryParam.variationId) ||
          (queryParam.variationId &&
            !variations
              .map((group) => group.id)
              .includes(queryParam.variationId)))
      ) {
        return {
          isDisabled: true,
          reason:
            "Select a test group for each audience targeting condition set to assign to a specific test group",
        };
      }
    }
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const shippingRateValidator = (variations: VariationFe[]) => {
  if (variations) {
    const isCustomRate = variations.some((group) =>
      group.shippingRateGroups!.some(
        (rateGroup) => rateGroup.groupType === "custom"
      )
    );
    if (isCustomRate) {
      return {
        isDisabled: false,
        reason: null,
      };
    } else {
      const hasEmptyShippingRates = variations?.some(
        (group) => group.shippingRateGroups!.length < 1
      );
      return {
        isDisabled: hasEmptyShippingRates,
        reason: hasEmptyShippingRates
          ? "Create rates for each test group"
          : null,
      };
    }
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

const renderQueryText = (query: ExpressionNodeQueryType) => {
  if (query.key === "screenLayout" || query.key === "device") {
    return `Device type is ${query.value}`;
  } else if (query.key === "visitor") {
    return `Visitor is ${query.value}`;
  } else if (query.key === "jsExpression") {
    return `(${query.value}) evaluates to true`;
  }

  return `${query.key} ${lowerCase(query.filter || "")} ${
    query.filter !== "isNotNull" && query.filter !== "isNull" ? query.value : ""
  }`;
};

export const renderConditionText = (queryParam: AudienceFiltersDto) => {
  if (queryParam.expression?.length) {
    const infix = postfixToInfix(queryParam.expression);
    let result = "";
    for (const node of infix) {
      if (node.operator) {
        if (node.operator === "start_paren") result += "(";
        else if (node.operator === "end_paren") result += ")";
        else result += ` ${node.operator} `;
      } else if (node.query) {
        result += `${renderQueryText(node.query)}`;
      }
    }
    return result;
  }
  let keyText = queryParam!.expression![0].query!.key;
  if (
    queryParam!.expression![0].query!.key === "screenLayout" ||
    queryParam!.expression![0].query!.key === "device"
  ) {
    keyText = "Device type";
  } else if (queryParam!.expression![0].query!.key === "visitor") {
    keyText = "Visitor is";
  }

  if (queryParam!.expression![0].query!.key === "visitor") {
    return `${keyText} is ${queryParam!.expression![0].query!.value}`;
  }

  if (queryParam!.expression![0].query!.key === "jsExpression") {
    return `(${queryParam!.expression![0].query!.value}) evaluates to true`;
  }

  return `${keyText} ${lowerCase(
    queryParam!.expression![0].query!.filter || ""
  )} ${
    queryParam!.expression![0].query!.filter !== "isNotNull" &&
    queryParam!.expression![0].query!.filter !== "isNull"
      ? queryParam!.expression![0].query!.value
      : ""
  } `;
};

export const renderTargetExperimentBehavior = (
  variations: VariationFe[],
  queryParam: AudienceFiltersDto,
  entity: "experiment" | "campaign"
) => {
  switch (queryParam.action as AudienceAction) {
    case "experienceInclude":
      return `include in ${entity}`;

    case "randomVariation":
      return "assign to random group";
    case "assignVariation":
      const assignedGroup = variations.find(
        (group) => group.id === queryParam.variationId
      );
      if (assignedGroup) {
        return `assign to ${assignedGroup.name}${
          queryParam.excludeFromAnalytics ? " and exclude from analytics" : ""
        }`;
      }
      return `assign to specific group${
        queryParam.excludeFromAnalytics ? " and exclude from analytics" : ""
      }`;
    case "experienceExclude":
      return `exclude from ${entity}`;
    case "experienceUnassigned":
      return "leave unassigned";
    case "leaveUnassigned":
      return "leave unassigned";
    default:
      return "assign to random group";
  }
};

export const redirectValidator = (
  contentFlowType: ExperienceType,
  specificRedirectErrors: string[],
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  if (specificRedirectErrors.length > 0) {
    return {
      isDisabled: true,
      reason: "Fix all redirect errors before continuing",
    };
  }

  const urlRedirects = getSpecificRedirectsUtil(redirectsState, "url");
  if (contentFlowType === "content/url" || urlRedirects.length) {
    if (urlRedirects.length === 0) {
      return { isDisabled: true, reason: "Create URL redirects" };
    }
    if (
      urlRedirects.some(
        (redirect) =>
          redirect.queryParams.length > 0 &&
          redirect.queryParams.some(
            (queryParam) => !queryParam.key || queryParam.key.length === 0
          )
      )
    ) {
      return {
        isDisabled: true,
        reason: "Fill out all redirect query parameters",
      };
    }

    if (
      urlRedirects.some(
        (redirect) =>
          redirect.originUrl.length === 0 ||
          (!redirect.destinationUrl && !redirect.skip) ||
          (redirect.destinationUrl &&
            redirect.destinationUrl.length === 0 &&
            !redirect.skip) ||
          (redirect.redirectType === "partialReplacement" &&
            !redirect.skip &&
            (!redirect.find || redirect.find.length === 0))
      )
    ) {
      return {
        isDisabled: true,
        reason: "Fill out all redirect URL inputs",
      };
    }
  }

  return { isDisabled: false, reason: null };
};

export const templateValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const templateRedirects = redirectsState.filter(
    (r) => r.isTemplateTest && !r.skip
  );
  if (templateRedirects.length) {
    for (const templateRedirect of templateRedirects) {
      if (!templateRedirect.queryParams[0]?.value) {
        return {
          isDisabled: true,
          reason: "Select a template for all test groups",
        };
      }
    }
  }
  if (contentFlowType === "content/template") {
    const hasNoTemplateRedirects =
      getSpecificRedirectsUtil(redirectsState, "template").length === 0;
    return {
      isDisabled: hasNoTemplateRedirects,
      reason: hasNoTemplateRedirects
        ? "Select a template for each group"
        : null,
    };
  }
  return { isDisabled: false, reason: null };
};

export const themeValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const themeRedirects = redirectsState.filter((r) => r.isThemeTest && !r.skip);
  if (themeRedirects.length) {
    for (const themeRedirect of themeRedirects) {
      if (!themeRedirect.queryParams[0]?.value) {
        return {
          isDisabled: true,
          reason: "Select a theme for all test groups",
        };
      }
    }
  }
  if (contentFlowType === "content/theme") {
    const hasNoThemeRedirects =
      getSpecificRedirectsUtil(redirectsState, "theme").length === 0;
    return {
      isDisabled: hasNoThemeRedirects,
      reason: hasNoThemeRedirects ? "Select a theme for each group" : null,
    };
  }
  return { isDisabled: false, reason: null };
};

export const themeOrTemplateValidator = (
  contentFlowType: ExperienceType,
  redirectsState: RedirectDto[]
): { isDisabled: boolean; reason: string | null } => {
  const isThemeNotValid = themeValidator(contentFlowType, redirectsState);
  const isTemplateNotValid = templateValidator(contentFlowType, redirectsState);
  if (isThemeNotValid.isDisabled) {
    return isThemeNotValid;
  }
  if (isTemplateNotValid.isDisabled) {
    return isTemplateNotValid;
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const experienceDetailsValidator = (
  experience: ExperienceFe
): { isDisabled: boolean; reason: string | null } => {
  if (!experience?.name) {
    return {
      isDisabled: true,
      reason: "Provide a name for this experiment",
    };
  }
  if (!experience?.experienceAnalysis.goal && !experience.endedAtTs) {
    return {
      isDisabled: true,
      reason: "Provide a goal for this experiment",
    };
  }
  if (
    experience.testTypes.hasTestPricing &&
    (!experience.experienceAnalysis.analyticsViewType ||
      experience.experienceAnalysis.analyticsViewType === "none") &&
    !experience.endedAtTs
  ) {
    return {
      isDisabled: true,
      reason: "Select a default view for analysis",
    };
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const variationDetailsValidator = (
  variations: VariationFe[]
): { isDisabled: boolean; reason: string | null } => {
  if ((variations || []).length <= 1) {
    return {
      isDisabled: true,
      reason: "Create at least two test groups",
    };
  }
  if (hasStringDuplicates((variations || []).map((tg) => tg.name))) {
    return {
      isDisabled: true,
      reason: "Provide a unique name for each test group",
    };
  }
  return {
    isDisabled: false,
    reason: null,
  };
};

export const productPickerValidator = (
  productPickerState: ProductPickerState,
  id: string | undefined
) => {
  if (id) {
    return {
      isDisabled: false,
      reason: null,
    };
  } else {
    const noProductsSelected = productPickerState.selectedProducts.length === 0;

    return {
      isDisabled: noProductsSelected,
      reason: noProductsSelected ? "Select at least one product" : null,
    };
  }
};

export const googleAnalyticsValidator = (
  isGoogleAnalyticsNextDisabled: boolean
) => {
  return {
    isDisabled: isGoogleAnalyticsNextDisabled,
    reason: isGoogleAnalyticsNextDisabled
      ? "Allow Google Analytics to load before continuing"
      : null,
  };
};

export const isAudienceTargetingEnabled = (trafficConfig: AudienceDto) => {
  return trafficConfig.filters.length > 0;
};
